import { supabase } from './supabase.ts';

export const tenantService = {
	async addTenant(data: any) {
		try {
			// Create tenant
			const { data: tenant, error: tenantError } = await supabase
				.from('tenants')
				.insert([data])
				.select()
				.single();

			if (tenantError) throw tenantError;

			// Generate initial bill
			const currentMonth = new Date().toLocaleString('default', {
				month: 'long',
				year: 'numeric',
			});
			const dueDate = new Date();
			dueDate.setMonth(dueDate.getMonth() + 1);
			dueDate.setDate(0); // Last day of current month

			const { error: billError } = await supabase.from('bills').insert([
				{
					tenant_id: tenant.id,
					property_id: data.property_id,
					amount: data.monthly_rent,
					month: currentMonth,
					due_date: dueDate.toISOString().split('T')[0],
					status: 'Due',
				},
			]);

			if (billError) throw billError;

			return tenant;
		} catch (error) {
			console.error('Error in addTenant:', error);
			throw error;
		}
	},

	async deleteTenant(id: string) {
		try {
			// First, delete related documents
			const { error: docError } = await supabase
				.from('tenant_documents')
				.delete()
				.eq('tenant_id', id);

			if (docError) throw docError;

			// Delete related payments
			const { error: paymentsError } = await supabase
				.from('payments')
				.delete()
				.eq('tenant_id', id);

			if (paymentsError) throw paymentsError;

			// Delete related bills
			const { error: billsError } = await supabase
				.from('bills')
				.delete()
				.eq('tenant_id', id);

			if (billsError) throw billsError;

			// Get property ID before deleting tenant
			const { data: tenantData } = await supabase
				.from('tenants')
				.select('property_id')
				.eq('id', id)
				.single();

			// Finally delete the tenant
			const { error: tenantError } = await supabase
				.from('tenants')
				.delete()
				.eq('id', id);

			if (tenantError) throw tenantError;

			// Update property status to Vacant
			if (tenantData?.property_id) {
				const { error: propertyError } = await supabase
					.from('properties')
					.update({ status: 'Vacant' })
					.eq('id', tenantData.property_id);

				if (propertyError) throw propertyError;
			}

			return { success: true };
		} catch (err) {
			console.error('Error deleting tenant:', err);
			throw new Error(`Failed to delete tenant: ${err.message}`);
		}
	},

	async deboardTenant(id: string, settlementData: any) {
		try {
			const { data: tenant, error: tenantFetchError } = await supabase
				.from('tenants')
				.select('property_id')
				.eq('id', id)
				.single();

			if (tenantFetchError) throw tenantFetchError;
			if (!tenant) throw new Error('Tenant not found');

			// Update all pending bills status
			const { error: billsError } = await supabase
				.from('bills')
				.update({
					status: 'Paid',
					paid_amount: (b: any) => b.amount,
					payment_date: new Date().toISOString().split('T')[0]
				})
				.eq('tenant_id', id)
				.in('status', ['Unpaid', 'Partial']);

			if (billsError) throw billsError;

			// Create payment record if additional payment exists
			if (settlementData.additional_payment > 0) {
				const { error: paymentError } = await supabase
					.from('payments')
					.insert([{
						tenant_id: id,
						property_id: tenant.property_id,
						total_amount: settlementData.additional_payment,
						payment_date: settlementData.settlement_date,
						payment_mode: settlementData.payment_mode,
						payment_to: settlementData.payment_to,
						notes: settlementData.payment_notes,
						settlement_payment: true,
						bill_id: null
					}]);

				if (paymentError) throw paymentError;
			}

			// Create settlement record
			const { error: settlementError } = await supabase
				.from('tenant_settlements')
				.insert([{
					tenant_id: id,
					property_id: tenant.property_id,
					total_amount: settlementData.total_amount,
					paid_amount: settlementData.paid_amount,
					outstanding_amount: settlementData.outstanding_amount,
					security_deposit: settlementData.security_deposit,
					additional_payment: settlementData.additional_payment,
					settlement_date: settlementData.settlement_date,
					settlement_notes: settlementData.settlement_notes,
					status: settlementData.status
				}]);

			if (settlementError) throw settlementError;

			// Update tenant status and property status
			await Promise.all([
				supabase
					.from('tenants')
					.update({ status: 'Inactive', deboarding_date: settlementData.settlement_date })
					.eq('id', id),
				supabase
					.from('properties')
					.update({ status: 'Vacant' })
					.eq('id', tenant.property_id)
			]);

			return { success: true };
		} catch (error) {
			console.error('Error in deboardTenant:', error);
			throw error;
		}
	},

	async getOutstandingSettlements() {
		try {
			// First get all settlements with basic info
			const { data: settlements, error } = await supabase
				.from('tenant_settlements')
				.select(`
					id,
					total_amount,
					paid_amount,
					outstanding_amount,
					settlement_date,
					settlement_notes,
					status,
					created_at,
						tenant_id,
					property_id
				`)
				.order('created_at', { ascending: false });

			if (error) throw error;

			// Then get tenant and property details separately
			const enrichedSettlements = await Promise.all((settlements || []).map(async (settlement) => {
				// Get tenant details
				const { data: tenant } = await supabase
					.from('tenants')
					.select('id, full_name, phone, email')
					.eq('id', settlement.tenant_id)
					.single();

				// Get property details
				const { data: property } = await supabase
					.from('properties')
					.select('id, name')
					.eq('id', settlement.property_id)
					.single();

				return {
					...settlement,
					tenant,
					property
				};
			}));

			return enrichedSettlements;
		} catch (err) {
			console.error('Error in getOutstandingSettlements:', err);
			throw err;
		}
	},

	async deleteTenantSettlements(tenantId: string) {
		try {
			const { error } = await supabase
				.from('tenant_settlements')
				.delete()
				.eq('tenant_id', tenantId);

			if (error) throw error;
			return { success: true };
		} catch (err: any) {
			console.error('Error deleting settlements:', err);
			throw err;
		}
	},
};
